/**
 * The closure verbs (13-21): FIELDSET §1.8 as pure builders on 13-20's engine, so `ClosedOn` and `ClosureStatus`
 * can only be written the way §1.8 says.
 *
 *   - `readyForApproval` (Incidents, D-13-L-3): the CareManager's closure assessment, `Actioning` → `Evaluating`;
 *   - `approveAndClose` (Incidents): `Evaluating` → `Completed`, the approval, `ClosedOn` and `ClosureStatus`;
 *     never by the preparer, never with the preparer unknown (D-13-L-19, D-13-21-1); when an investigation was
 *     required, the approver's report scrutiny is written as the `Review` row (G-60) in the same save, and the
 *     approval is refused without it (ISS-260, D-13-33). Never while a verification check is open, and never with
 *     the checks unknown (13.2-05, D-13.2-P-8: `ctx.openChecks`, resolved by the caller from 13.2-06's checklist).
 *     `readyForApproval` may carry a `Communication` row (G-67);
 *   - `returnForRework` (Incidents, ISS-259 / D-13-33): `Evaluating` → `Actioning`, a reason required; Oversight's
 *     way to send an assessment back to the Care Manager instead of approving it or cancelling the incident;
 *   - `close` (Complaints, Feedback, CI): `Actioning` / `Evaluating` → `Completed`; a CI must be approved first;
 *   - `ciApprove` (CI): `ResolutionApprovedByUPN` = the actor;
 *   - `cancel` (every register): → `Cancelled`, `ClosureStatus` closed, `ClosedOn` never written (§1.8);
 *   - `closeAsDuplicate` (Incidents, 13.2-05, D-13.2-P-10): a cancel that names the record kept (`DuplicateOfRef`),
 *     never touching `ExternalId` (the intake reconcile's key), with a `Note` row for the kept record (`keptSiblings`);
 *   - `reopen` (every register, D-13-L-17): `Completed` → `Actioning`, `ClosedOn` cleared, `ClosureStatus` open;
 *   - `riskReview` / `riskRetire` (Risk): the next review day, a `Review` row, acceptance; retire = `Completed`.
 *
 * Every builder takes `(row, actor, input, now, ctx)` and returns 13-20's shape (`RecordVerbBuild`): the PATCH,
 * one `StateChange` draft per changed column and any `Review` row, tagged `[a:<id>]` / `[a:<id>.<n>]` as one save;
 * or every refusal at once. The diff, the drafts, the tags, the value checks and the Note screening are 13-20's
 * (`diffRecord`, `draftStateChanges`, `finaliseDrafts`, `cleanColumn`, `cleanNoteText`); the no-self-approval rule
 * is 13-22's (`checkPreparer`). Roles are 13-22's: these builders check state and content only.
 *
 * Column names and lists come from 13-17's field set: the few named here are checked against it at load, and the
 * closure-required set is read from each column's `closure` rule (FIELDSET §2 plus §3.4's "before closure"),
 * never retyped. Closure Links are never read (D-13-L-14): `ctx.openLinks` comes back as warnings for the confirm.
 *
 * Pure: no Graph, no React, no `Date.now()`; `now`, `input.auditId` and `ctx` come from the caller. A programming
 * error (a verb the register lacks, a missing audit id, an unknown verb) throws `RecordVerbError`.
 */

import { liveChoice } from '../config/fieldset/live';
import { PENDING_13_2, REGISTER_FIELDS, RECORD_REGISTERS, VERB_ORDER, fieldOf, verbColumns, verbsOf } from '../config/fieldset/registerFields';
import { LOG_KINDS, LOG_KIND_MAP, type SiblingWriter } from '../config/fieldset/siblings';
import type { AuditEntryType } from '../types/qms';
import type { RecordFieldValue, RecordRegister, RecordRow, RecordVerbKey, Refusal, RequiredRule, SiblingDraft, VerbResult } from '../types/records';
import { checkUpn, dayExisting, dayNew, melbourneDayOf, notInFuture, toInstant, todayMelbourne } from './fieldValues';
import { describeRule } from './lodgeBuilder';
import { RECORD_AUTHORITY_TEXT, checkPreparer } from './recordAuthority';
import { LIVE_REF_PATTERN, PRACTICE_REF_PATTERN, isPracticeRef, parsePracticeRef, parseRef } from './recordQuery';
import {
  ENGINE_VERBS,
  RecordVerbError,
  cleanColumn,
  cleanNoteText,
  diffRecord,
  draftStateChanges,
  finaliseDrafts,
  holds,
  isBlank,
  isUnanswered,
  type DraftContext,
  type PendingDraft,
  type RecordActor,
  type RecordRefusalCode,
  type RecordVerbBuild,
} from './recordVerbs';

// ---------------------------------------------------------------------------
// The names this module writes (each checked against the field set below)
// ---------------------------------------------------------------------------

const STATUS = 'Status';
const CLOSED_ON = 'ClosedOn';
const CLOSURE_STATUS = 'ClosureStatus';
const RESOLUTION_APPROVED_ON = 'ResolutionApprovedOn';
const RESOLUTION_APPROVED_BY_UPN = 'ResolutionApprovedByUPN';
const REVIEW_DATE = 'ReviewDate';
const RISK_ACCEPTED = 'RiskAccepted';
const RISK_ACCEPTED_BY_UPN = 'RiskAcceptedByUPN';
const RISK_ACCEPTED_ON = 'RiskAcceptedOn';
/** FIELDSET §3.4.1: the `LogKind` of the Risk review row (G-151, G-196) and the Incidents report scrutiny (G-60), CLO. */
const REVIEW = 'Review';
/** FIELDSET §3.4.1: the Incidents `Communication` row (G-67: what was told to the people affected, and when), CLO. */
const COMMUNICATION = 'Communication';
const INVESTIGATION_REQUIRED = 'InvestigationRequired';
/** 13.2-01 / D-13.2-P-10: the kept record's `INC-<Id>`, written only by `closeAsDuplicate`. */
const DUPLICATE_OF_REF = 'DuplicateOfRef';
/** The intake's ShiftCare ticket id: the daily reconcile's key. Read for the kept record's note, never written. */
const EXTERNAL_ID = 'ExternalId';
const NOTE: AuditEntryType = 'Note';

const [S_NEW, S_ACTIONING, S_EVALUATING, S_COMPLETED, S_CANCELLED] = liveChoice(
  'incidents',
  STATUS,
  'New',
  'Actioning',
  'Evaluating',
  'Completed',
  'Cancelled',
);
const [CLOSURE_OPEN, CLOSURE_CLOSED] = liveChoice('incidents', CLOSURE_STATUS, 'Open', 'Closed');
const [RISK_YES] = liveChoice('risk', RISK_ACCEPTED, 'Yes');
const [INVESTIGATION_YES] = liveChoice('incidents', INVESTIGATION_REQUIRED, 'Yes');

/** FIELDSET §4.0: the closure verbs' writer code (the §3.4.1 map gives `Review` and `Communication` to CLO). */
export const CLOSURE_WRITER: SiblingWriter = 'CLO';

/** The shortest reason a cancel, reopen or retire accepts (characters, after cleaning). */
export const REASON_MIN = 10;

// ---------------------------------------------------------------------------
// The verbs and their moves
// ---------------------------------------------------------------------------

export type ClosureVerb = Extract<
  RecordVerbKey,
  | 'readyForApproval'
  | 'approveAndClose'
  | 'returnForRework'
  | 'close'
  | 'ciApprove'
  | 'riskReview'
  | 'riskRetire'
  | 'cancel'
  | 'closeAsDuplicate'
  | 'reopen'
>;

/**
 * The closure verbs, in 13-17's verb order (so the record page lists them in that order). A verb whose builder is not
 * written yet (`PENDING_13_2`, 13.2-02) is left out until its plan adds it; the set is empty since 13.2-05 built
 * `closeAsDuplicate`.
 */
export const CLOSURE_VERBS: readonly ClosureVerb[] = VERB_ORDER.filter(
  (v): v is ClosureVerb => v !== 'lodge' && !(ENGINE_VERBS as readonly string[]).includes(v) && !PENDING_13_2.has(v),
);

export interface ClosureTransition {
  /** The `Status` values the verb runs from. */
  readonly from: readonly string[];
  /** The `Status` it sets, or `null` for a verb that moves none. */
  readonly to: string | null;
}

/**
 * FIELDSET §1.8's moves, one table for the builders, 13-22 and the record page (13-26). The `Status` values are the
 * same on all five registers (checked at load).
 */
export const CLOSURE_TRANSITIONS: Readonly<Record<ClosureVerb, ClosureTransition>> = {
  readyForApproval: { from: [S_ACTIONING], to: S_EVALUATING },
  approveAndClose: { from: [S_EVALUATING], to: S_COMPLETED },
  returnForRework: { from: [S_EVALUATING], to: S_ACTIONING },
  close: { from: [S_ACTIONING, S_EVALUATING], to: S_COMPLETED },
  ciApprove: { from: [S_NEW, S_ACTIONING, S_EVALUATING], to: null },
  riskReview: { from: [S_NEW, S_ACTIONING, S_EVALUATING], to: null },
  riskRetire: { from: [S_NEW, S_ACTIONING, S_EVALUATING], to: S_COMPLETED },
  cancel: { from: [S_NEW, S_ACTIONING, S_EVALUATING], to: S_CANCELLED },
  closeAsDuplicate: { from: [S_NEW, S_ACTIONING, S_EVALUATING], to: S_CANCELLED },
  reopen: { from: [S_COMPLETED], to: S_ACTIONING },
};

/** The verb as a person reads it, at the start of each sibling `Note` ("Cancel by …"). */
export const CLOSURE_LABELS: Readonly<Record<ClosureVerb, string>> = {
  readyForApproval: 'Ready for approval',
  approveAndClose: 'Approve and close',
  returnForRework: 'Return to the Care Manager',
  close: 'Close',
  ciApprove: 'Approve',
  riskReview: 'Risk review',
  riskRetire: 'Retire',
  cancel: 'Cancel',
  closeAsDuplicate: 'Close as a duplicate',
  reopen: 'Reopen',
};

/** The input keys each verb takes; any other key given is refused `not-applicable`. */
type InputKey = 'set' | 'reason' | 'closedOn' | 'nextReviewOn' | 'reviewNote' | 'communicationNote' | 'keptRef';

const INPUT_KEYS: readonly InputKey[] = ['set', 'reason', 'closedOn', 'nextReviewOn', 'reviewNote', 'communicationNote', 'keptRef'];

const TAKES: Readonly<Record<ClosureVerb, readonly InputKey[]>> = {
  readyForApproval: ['set', 'reason', 'communicationNote'],
  approveAndClose: ['closedOn', 'reviewNote'],
  returnForRework: ['reason'],
  close: ['set', 'reason', 'closedOn'],
  ciApprove: [],
  riskReview: ['set', 'reason', 'nextReviewOn', 'reviewNote'],
  riskRetire: ['reason'],
  cancel: ['reason'],
  closeAsDuplicate: ['keptRef', 'reason'],
  reopen: ['reason'],
};

/** Columns a verb sets itself; a value the person supplies is refused `stamped-column`. */
const SET_BY_VERB: Readonly<Partial<Record<ClosureVerb, readonly string[]>>> = {
  approveAndClose: [RESOLUTION_APPROVED_ON, RESOLUTION_APPROVED_BY_UPN],
  ciApprove: [RESOLUTION_APPROVED_BY_UPN],
  riskReview: [REVIEW_DATE, RISK_ACCEPTED_BY_UPN, RISK_ACCEPTED_ON],
};

/** Always set by the closure verbs, never through `set`. */
const ALWAYS_SET_BY_VERB: readonly string[] = [STATUS, CLOSURE_STATUS, CLOSED_ON];

/** The verbs whose reason is required, at least `REASON_MIN` characters (D-13-21-7). */
export const REASON_REQUIRED: readonly ClosureVerb[] = ['returnForRework', 'riskRetire', 'cancel', 'reopen'];

/** The column each dedicated input key stands for, so the record page places its refusals at that input (13-26). */
export const CLOSURE_INPUT_COLUMN: Readonly<Partial<Record<InputKey, string>>> = {
  closedOn: CLOSED_ON,
  nextReviewOn: REVIEW_DATE,
  keptRef: DUPLICATE_OF_REF,
};

/** The input keys a closure builder takes. */
export type ClosureInputKey = InputKey;

/** `readyForApproval` checks the closure-required set except what approve-and-close itself writes (the plan). */
const AFTER_APPROVAL: ReadonlySet<string> = new Set([RESOLUTION_APPROVED_ON, RESOLUTION_APPROVED_BY_UPN, CLOSED_ON, STATUS, CLOSURE_STATUS]);

// ---------------------------------------------------------------------------
// Load-time checks: every name above is the field set's, every move is live
// ---------------------------------------------------------------------------

for (const register of RECORD_REGISTERS) {
  liveChoice(register, STATUS, S_NEW, S_ACTIONING, S_EVALUATING, S_COMPLETED, S_CANCELLED);
  liveChoice(register, CLOSURE_STATUS, CLOSURE_OPEN, CLOSURE_CLOSED);
  for (const verb of verbsOf(register)) {
    if (PENDING_13_2.has(verb)) continue; // 13.2-03 / 13.2-05 write these builders
    if (verb !== 'lodge' && !(ENGINE_VERBS as readonly string[]).includes(verb) && !CLOSURE_VERBS.includes(verb as ClosureVerb)) {
      throw new Error(`recordClosure: ${register} verb ${verb} has no closure builder`);
    }
  }
  for (const verb of ['close', 'approveAndClose'] as const) {
    if (verbsOf(register).includes(verb) && !verbColumns(register, verb).includes(CLOSED_ON)) {
      throw new Error(`recordClosure: ${register} ${verb} does not write ${CLOSED_ON}`);
    }
  }
  for (const verb of ['cancel', 'reopen'] as const) {
    for (const col of [STATUS, CLOSURE_STATUS]) {
      if (!verbColumns(register, verb).includes(col)) throw new Error(`recordClosure: ${register} ${verb} lacks ${col}`);
    }
  }
}
for (const [register, verb, cols] of [
  ['incidents', 'approveAndClose', [STATUS, CLOSED_ON, CLOSURE_STATUS, RESOLUTION_APPROVED_ON, RESOLUTION_APPROVED_BY_UPN]],
  ['incidents', 'readyForApproval', [STATUS]],
  ['ci', 'ciApprove', [RESOLUTION_APPROVED_BY_UPN]],
  ['ci', 'reopen', [RESOLUTION_APPROVED_BY_UPN]],
  ['risk', 'riskReview', [REVIEW_DATE, RISK_ACCEPTED, RISK_ACCEPTED_BY_UPN, RISK_ACCEPTED_ON]],
  ['risk', 'riskRetire', [STATUS, CLOSURE_STATUS]],
] as const) {
  for (const col of cols) {
    if (!verbColumns(register, verb).includes(col)) throw new Error(`recordClosure: ${register} ${verb} lacks ${col}`);
  }
}
for (const [kind, register] of [[REVIEW, 'risk'], [REVIEW, 'incidents'], [COMMUNICATION, 'incidents']] as const) {
  if (!LOG_KINDS.includes(kind) || !LOG_KIND_MAP[kind]?.registers.includes(register) || !LOG_KIND_MAP[kind].writers.includes(CLOSURE_WRITER)) {
    throw new Error(`recordClosure: the ${kind} row is not a CLO row on ${register} (FIELDSET §3.4.1)`);
  }
}
if (!verbColumns('incidents', 'triage').includes(INVESTIGATION_REQUIRED)) {
  throw new Error(`recordClosure: ${INVESTIGATION_REQUIRED} is not an Incidents triage column`);
}
// 13.2-05 (D-13.2-P-10): close-as-duplicate writes exactly the cancel's two columns and the link, and never the
// reconcile key or a close day.
if (verbColumns('incidents', 'closeAsDuplicate').join() !== [STATUS, CLOSURE_STATUS, DUPLICATE_OF_REF].join()) {
  throw new Error(`recordClosure: incidents closeAsDuplicate is not ${STATUS}, ${CLOSURE_STATUS}, ${DUPLICATE_OF_REF}`);
}
if (fieldOf('incidents', DUPLICATE_OF_REF)?.kind !== 'text' || fieldOf('incidents', EXTERNAL_ID) !== undefined) {
  throw new Error(`recordClosure: ${DUPLICATE_OF_REF} must be a text column and ${EXTERNAL_ID} no verb's column`);
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The refusal codes this module adds to 13-20's. */
export type ClosureRefusalCode =
  | RecordRefusalCode
  | 'invalid-transition'
  | 'reason-required'
  | 'closure-field-blank'
  | 'not-approved'
  | 'self-approval'
  | 'preparer-unknown'
  | 'before-created'
  | 'review-not-after-today'
  | 'review-required'
  // 13.2-05: the verification gate on approve-and-close (D-13.2-P-8)
  | 'checks-unknown'
  | 'checks-open'
  // 13.2-05: close-as-duplicate (D-13.2-P-10); `record-closed` is 13.2-03's
  | 'bad-ref'
  | 'dup-self'
  | 'dup-not-found'
  | 'dup-wrong-register'
  | 'dup-kept-cancelled'
  | 'dup-chain';

/** 13.2-05: the words of the approve gate's unknown-checks refusal (the closure panel shows the same, 13.2-08). */
export const CHECKS_UNKNOWN_MESSAGE = "The verification checks couldn't be read, so this can't be closed yet. Open Verify.";

/**
 * What the person saves. `set` (by SharePoint internal name, as 13-20) holds the closure columns the verb takes
 * from the person. `closedOn` is the close day (`YYYY-MM-DD`, Melbourne; default today). `nextReviewOn` and
 * `reviewNote` are the Risk review's; `reviewNote` is also approve-and-close's report scrutiny (the Incidents
 * `Review` row, G-60). `communicationNote` is ready-for-approval's optional `Communication` row (G-67). `reason` is
 * required on return, cancel, reopen and retire, and clears a column elsewhere (D-13-20-1). `auditId` is the
 * `[a:<id>]` tag.
 */
export interface ClosureInput {
  set?: Readonly<Record<string, unknown>>;
  reason?: string;
  closedOn?: string;
  nextReviewOn?: string;
  reviewNote?: string;
  communicationNote?: string;
  /** `closeAsDuplicate` (13.2-05): the record kept, as `INC-<id>` (case and spaces forgiven; stored canonical). */
  keptRef?: string;
  auditId: string;
}

/** Facts the caller resolved. Nothing in it can widen what a builder allows. */
export interface ClosureContext {
  /** `approveAndClose`: who ran ready for approval (the latest `Status → Evaluating` row's `ActorUPN`, D-13-L-19). */
  preparedByUpn?: string;
  /** The record's open Closure Links (refs), named in the confirm; never a block (D-13-L-14). */
  openLinks?: readonly string[];
  /**
   * `approveAndClose` (13.2-05, D-13.2-P-8): the verification checks still open, as the caller resolved them from
   * 13.2-06's checklist (`openChecks(incidentChecklist(row, followUps, now))`). `[]` = all pass. Absent = unknown,
   * which is refused (fail closed, like the preparer). The builder never recomputes them.
   */
  openChecks?: readonly string[];
  /** `closeAsDuplicate` (13.2-05): the kept record's row as the caller read it (by `input.keptRef`), or null. */
  kept?: RecordRow | null;
}

/** `closeAsDuplicate`'s build: as every closure verb's, plus the kept record's `Note` row, for its own trail. */
export type DuplicateBuild =
  | ({ ok: true; warnings: Refusal[]; keptSiblings: SiblingDraft[] } & VerbResult)
  | { ok: false; refusals: Refusal[]; warnings: Refusal[] };

export type ClosureBuilder = (
  row: RecordRow,
  actor: RecordActor,
  input: ClosureInput,
  now: Date,
  ctx?: ClosureContext,
) => RecordVerbBuild;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * The closure-required set: every column whose closure rule is not `optional` (FIELDSET §2 `closure`, conditionals
 * included, and §3.4's "before closure", which 13-17 transcribed as `closure`). Never a Person column (§1.2).
 */
export function closureRequiredColumns(register: RecordRegister): readonly string[] {
  return REGISTER_FIELDS[register]
    .filter((f) => f.required.closure !== undefined && f.required.closure !== 'optional' && f.kind !== 'person-frozen')
    .map((f) => f.name);
}

/** The input keys a closure verb takes (the record page draws its inputs from this, 13-26). */
export function closureTakes(verb: ClosureVerb): readonly ClosureInputKey[] {
  return TAKES[verb];
}

/**
 * The columns the person answers for a closure verb (its `set`, 13-26): the verb's own list, less `Status`,
 * `ClosureStatus`, `ClosedOn` and the columns the verb stamps itself; never a frozen Person column or attachments.
 * Empty for a verb that takes no `set`.
 */
export function closureInputColumns(register: RecordRegister, verb: ClosureVerb): string[] {
  if (!TAKES[verb].includes('set')) return [];
  const skip = new Set([...ALWAYS_SET_BY_VERB, ...(SET_BY_VERB[verb] ?? [])]);
  return verbColumns(register, verb).filter((column) => {
    const kind = fieldOf(register, column)?.kind;
    return !skip.has(column) && kind !== undefined && kind !== 'person-frozen' && kind !== 'attachments';
  });
}

/** Does this verb end the record's open life (to `Completed` or `Cancelled`)? The confirm then names open links. */
export function closesRecord(verb: ClosureVerb): boolean {
  const to = CLOSURE_TRANSITIONS[verb].to;
  return to === S_COMPLETED || to === S_CANCELLED;
}

/** The closure verbs this record's register has whose `from` holds its `Status`, in verb order. */
export function closureVerbsFor(row: RecordRow): ClosureVerb[] {
  const current = row.fields[STATUS];
  return CLOSURE_VERBS.filter(
    (verb) => verbsOf(row.register).includes(verb) && typeof current === 'string' && CLOSURE_TRANSITIONS[verb].from.includes(current),
  );
}

// ---------------------------------------------------------------------------
// One save (shared by every builder)
// ---------------------------------------------------------------------------

interface Save {
  verb: ClosureVerb;
  row: RecordRow;
  register: RecordRegister;
  actor: RecordActor;
  actorUpn: string;
  input: ClosureInput;
  now: Date;
  refusals: Refusal[];
  warnings: Refusal[];
  /** The cleaned reason, `''` when none. */
  reason: string;
  reasonRefused: boolean;
  /** Columns already refused (a value or a date), so the required check does not refuse them twice. */
  bad: Set<string>;
  /** The `Status` this verb sets, or `null`. */
  to: string | null;
}

function refuse(save: Save, code: ClosureRefusalCode, column: string | undefined, message: string): void {
  save.refusals.push(column === undefined ? { code, message } : { code, column, message });
}

function given(input: ClosureInput, key: InputKey): boolean {
  const value = input[key];
  if (value === undefined) return false;
  if (key === 'set') return typeof value === 'object' && value !== null && Object.keys(value).length > 0;
  return true;
}

/** Checks every builder starts with: the verb and register, the audit id, the actor, the inputs, the move. */
function begin(verb: ClosureVerb, row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx: ClosureContext | undefined): Save {
  const register = row.register;
  if (!verbsOf(register).includes(verb)) {
    throw new RecordVerbError('verb-not-on-register', `recordClosure: ${register} has no ${verb} verb`);
  }
  if (!input || typeof input.auditId !== 'string' || input.auditId.trim() === '') {
    throw new RecordVerbError('audit-id-required', `recordClosure: input.auditId is required (the [a:<id>] tag)`);
  }
  const actorCheck = checkUpn(actor?.upn as string);
  const save: Save = {
    verb,
    row,
    register,
    actor,
    actorUpn: actorCheck.ok ? actorCheck.value : '',
    input,
    now,
    refusals: [],
    warnings: (ctx?.openLinks ?? []).map((ref) => ({
      code: 'open-link',
      message: `${ref} is still open and linked to this record. Closing doesn't wait for it.`,
    })),
    reason: '',
    reasonRefused: false,
    bad: new Set(),
    to: CLOSURE_TRANSITIONS[verb].to,
  };
  if (!actorCheck.ok) refuse(save, 'invalid-actor', undefined, 'The signed-in account has no usable email address.');

  for (const key of INPUT_KEYS) {
    if (given(input, key) && !TAKES[verb].includes(key)) {
      refuse(save, 'not-applicable', key, `${CLOSURE_LABELS[verb]} doesn't take ${key}.`);
    }
  }

  if (TAKES[verb].includes('reason') && input.reason !== undefined && input.reason !== null) {
    const r = cleanNoteText(input.reason, 'The reason');
    if (r.ok) save.reason = r.value;
    else {
      refuse(save, r.code, 'reason', r.message);
      save.reasonRefused = true;
    }
  }

  const current = row.fields[STATUS];
  if (verb === 'closeAsDuplicate' && (current === S_COMPLETED || current === S_CANCELLED)) {
    // 13.2-05: named as 13.2-03's complete names it, so the person reads why, not a move table.
    const reopen = current === S_COMPLETED ? ' Reopen it first if it is the duplicate.' : '';
    refuse(save, 'record-closed', STATUS, `This incident is already ${current}, so it can't be closed as a duplicate.${reopen}`);
  } else if (typeof current !== 'string' || !CLOSURE_TRANSITIONS[verb].from.includes(current)) {
    const from = CLOSURE_TRANSITIONS[verb].from.join(', ');
    refuse(save, 'invalid-transition', STATUS, `${CLOSURE_LABELS[verb]} works only on a record that is ${from}; this one is ${isBlank(current) ? 'blank' : String(current)}.`);
  }
  return save;
}

/** Return, cancel, reopen and retire: a reason of at least `REASON_MIN` characters. */
function requireReason(save: Save): void {
  if (save.reasonRefused) return;
  if (save.reason.length < REASON_MIN) {
    refuse(save, 'reason-required', 'reason', `Give a reason of at least ${REASON_MIN} characters; it goes on the record's trail.`);
  }
}

/**
 * The closure columns the person sets (`set`): only the verb's own list, never a column the verb sets itself;
 * each value cleaned by its kind (13-20's `cleanColumn`), and clearing an answered column needs a reason.
 */
function takeSet(save: Save): Record<string, RecordFieldValue> {
  const { register, verb } = save;
  const own = new Set(verbColumns(register, verb));
  const setByVerb = new Set([...ALWAYS_SET_BY_VERB, ...(SET_BY_VERB[verb] ?? [])]);
  const values: Record<string, RecordFieldValue> = {};
  for (const [key, raw] of Object.entries(save.input.set ?? {})) {
    if (raw === undefined) continue;
    if (setByVerb.has(key)) {
      refuse(save, 'stamped-column', key, `${fieldOf(register, key)?.label ?? key} is set by this step; it can't be entered.`);
      continue;
    }
    if (!own.has(key)) {
      refuse(save, 'not-this-verb', key, `${key} isn't recorded by ${CLOSURE_LABELS[verb].toLowerCase()}.`);
      continue;
    }
    const spec = fieldOf(register, key);
    if (!spec) throw new Error(`recordClosure: ${register} ${verb} column ${key} has no field spec`);
    const r = cleanColumn(register, spec, raw);
    if (r.kind === 'refused') {
      refuse(save, r.code, key, r.message);
      save.bad.add(key);
      continue;
    }
    if (r.kind === 'value' && r.warning) save.warnings.push(r.warning);
    values[key] = r.kind === 'value' ? r.value : null;
  }
  for (const ch of diffRecord(save.row, values).changes) {
    if (ch.cleared && !save.reason) {
      if (!save.reasonRefused) refuse(save, 'clear-needs-reason', ch.column, `Clearing ${fieldOf(register, ch.column)?.label ?? ch.column} needs a reason: a blank is never an answer.`);
      save.bad.add(ch.column);
      delete values[ch.column];
    }
  }
  return values;
}

/** A Melbourne day as a column's §1.4 payload: `YYYY-MM-DD` on a `day-new` column, UTC midnight on `day-existing`. */
function dayPayload(register: RecordRegister, column: string, day: string): string {
  const kind = fieldOf(register, column)?.kind;
  if (kind === 'day-new') return day;
  if (kind === 'day-existing') {
    const r = dayExisting(day);
    if (r.ok) return r.value;
  }
  throw new Error(`recordClosure: ${register}.${column} is not a day column`);
}

/**
 * The close day as `ClosedOn`'s payload, or `null` (refused): `input.closedOn` or Melbourne today; a real day, not
 * in the future, not before the record's `Created` Melbourne day.
 */
function closeDay(save: Save): string | null {
  const raw = save.input.closedOn;
  const today = todayMelbourne(save.now);
  const day = isBlank(raw) ? today : raw;
  const label = fieldOf(save.register, CLOSED_ON)?.label ?? CLOSED_ON;
  let problem: [ClosureRefusalCode, string] | null = null;
  if (typeof day !== 'string' || !dayNew(day.trim()).ok) problem = ['invalid-day', `${label}: enter a real date as YYYY-MM-DD.`];
  else if (!notInFuture(day.trim(), save.now).ok) problem = ['in-future', `${label} can't be in the future.`];
  else {
    const created = melbourneDayOf(typeof save.row.fields.Created === 'string' ? save.row.fields.Created : null);
    if (created !== null && day.trim() < created) problem = ['before-created', `${label} can't be before the record was created (${created}).`];
  }
  if (problem) {
    refuse(save, problem[0], CLOSED_ON, problem[1]);
    save.bad.add(CLOSED_ON);
    return null;
  }
  return dayPayload(save.register, CLOSED_ON, (day as string).trim());
}

/** The `Review` row (FIELDSET §3.4.1, CLO): the person's text, then who. */
function reviewRow(save: Save, text: string): PendingDraft {
  return logRow(save, REVIEW, text);
}

/** A closure verb's `Note` row of one `LogKind` (FIELDSET §3.4.1, CLO): the person's text, then who. */
function logRow(save: Save, logKind: string, text: string): PendingDraft {
  return {
    Title: `${save.verb} ${save.row.ref}: ${logKind}`,
    ParentRef: save.row.ref,
    EntryType: NOTE,
    LogKind: logKind,
    EntryTimestamp: toInstant(save.now),
    ActorUPN: save.actorUpn,
    ...(save.actor.siteUserId ? { ActorLookupId: save.actor.siteUserId } : {}),
    noteHead: `${text}\n${CLOSURE_LABELS[save.verb]} by ${save.actorUpn}.`,
  };
}

interface Required {
  /** `closure`: the closure-required set; `own`: the verb's own columns' closure rules; `none`. */
  readonly scope: 'closure' | 'own' | 'none';
  /** Columns not checked. */
  readonly skip?: ReadonlySet<string>;
  /** A column refused with its own code instead of `closure-field-blank`. */
  readonly codeOf?: Readonly<Record<string, ClosureRefusalCode>>;
}

/** Diff, check what must not be blank on the merged record, then draft and tag the save, or refuse it. */
function finish(save: Save, proposed: Record<string, RecordFieldValue>, required: Required, rows: PendingDraft[] = []): RecordVerbBuild {
  const { register, verb, row } = save;
  const order = verbColumns(register, verb);
  const ordered: Record<string, RecordFieldValue> = {};
  for (const column of order) if (column in proposed) ordered[column] = proposed[column];
  for (const column of Object.keys(proposed)) {
    if (!(column in ordered)) throw new Error(`recordClosure: ${verb} wrote ${column}, which is not one of its columns`);
  }
  const { patch, changes } = diffRecord(row, ordered);

  if (required.scope !== 'none') {
    const merged = (c: string): unknown => (c in patch ? patch[c] : row.fields[c]);
    const columns = required.scope === 'closure' ? closureRequiredColumns(register) : order;
    for (const column of columns) {
      if (save.bad.has(column) || required.skip?.has(column)) continue;
      const spec = fieldOf(register, column);
      const rule: RequiredRule | undefined = spec?.required.closure;
      if (!spec || spec.kind === 'person-frozen' || spec.kind === 'attachments') continue;
      if (!holds(register, rule, merged, save.bad) || !isUnanswered(register, column, merged(column))) continue;
      const when = typeof rule === 'object' ? ` when ${describeRule(register, rule)}` : '';
      const code = required.codeOf?.[column] ?? (required.scope === 'closure' ? 'closure-field-blank' : 'required');
      const message =
        code === 'not-approved'
          ? 'This improvement must be approved before it is closed.'
          : `${spec.label} is required${required.scope === 'closure' ? ' before closing' : ''}${when}.`;
      refuse(save, code, column, message);
    }
  }

  if (save.refusals.length > 0) return { ok: false, refusals: save.refusals, warnings: save.warnings };
  if (changes.length === 0 && rows.length === 0) {
    return { ok: false, refusals: [{ code: 'no-change', message: 'Nothing has changed, so there is nothing to save.' }], warnings: save.warnings };
  }
  const ctx: DraftContext = {
    verb,
    label: CLOSURE_LABELS[verb],
    actorUpn: save.actorUpn,
    ...(save.actor.siteUserId ? { actorLookupId: save.actor.siteUserId } : {}),
    now: save.now,
    ...(save.reason ? { reason: save.reason } : {}),
  };
  const siblings = finaliseDrafts([...draftStateChanges(row, changes, ctx), ...rows], save.input.auditId);
  return { ok: true, patch, siblings, warnings: save.warnings };
}

// ---------------------------------------------------------------------------
// The builders (FIELDSET §1.8)
// ---------------------------------------------------------------------------

/**
 * Incidents, step one (D-13-L-3): the closure assessment from `set`, `Actioning` → `Evaluating`. Refused while any
 * closure-required column other than what approve-and-close writes is blank on the merged record.
 */
export function buildReadyForApproval(row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx?: ClosureContext): RecordVerbBuild {
  const save = begin('readyForApproval', row, actor, input, now, ctx);
  const proposed: Record<string, RecordFieldValue> = { ...takeSet(save) };
  if (save.to) proposed[STATUS] = save.to;
  // G-67 (ISS-260, D-13-33): what was told to the people affected, and when. Optional: FIELDSET sets no rule.
  const rows: PendingDraft[] = [];
  if (input.communicationNote !== undefined && input.communicationNote !== null) {
    const note = cleanNoteText(input.communicationNote, 'What was communicated');
    if (!note.ok) refuse(save, note.code, 'communicationNote', note.message);
    else if (note.value !== '') rows.push(logRow(save, COMMUNICATION, note.value));
  }
  return finish(save, proposed, { scope: 'closure', skip: AFTER_APPROVAL }, rows);
}

/**
 * 13.2-05 (D-13.2-P-8): Oversight closes only when every verification check passes. `openChecks` is the caller's
 * (13.2-06's checklist, never recomputed here): absent → `checks-unknown`; non-empty → `checks-open`, naming each.
 */
function checkVerification(save: Save, openChecks: readonly string[] | undefined): void {
  if (!Array.isArray(openChecks)) {
    refuse(save, 'checks-unknown', undefined, CHECKS_UNKNOWN_MESSAGE);
    return;
  }
  if (openChecks.length === 0) return;
  refuse(save, 'checks-open', undefined, checksOpenMessage(openChecks));
}

/**
 * 13.2-08: the words of the `checks-open` refusal, so the closure panel shows exactly what the builder will say
 * before Approve is pressed.
 */
export function checksOpenMessage(openChecks: readonly string[]): string {
  const named = openChecks.map((c) => (typeof c === 'string' ? c.trim() : '')).filter((c) => c !== '');
  const list = named.length > 0 ? named.join('; ') : 'a check with no name';
  return `The verification checks aren't all done, so this can't be closed yet. Still open: ${list}. See Verify.`;
}

/**
 * Incidents, step two: only from `Evaluating`, never by the preparer and never with the preparer unknown
 * (13-22's `checkPreparer`), never while a verification check is open or with the checks unknown (13.2-05).
 * Approval today, `ClosedOn` the close day, `Completed`, closed; the whole closure-required set re-checked.
 */
export function buildApproveAndClose(row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx?: ClosureContext): RecordVerbBuild {
  const save = begin('approveAndClose', row, actor, input, now, ctx);
  const preparer = checkPreparer(ctx?.preparedByUpn, save.actorUpn);
  if (!preparer.ok) refuse(save, preparer.reason as ClosureRefusalCode, undefined, RECORD_AUTHORITY_TEXT[preparer.reason]);
  checkVerification(save, ctx?.openChecks);
  const proposed: Record<string, RecordFieldValue> = {};
  if (save.to) proposed[STATUS] = save.to;
  const day = closeDay(save);
  if (day !== null) proposed[CLOSED_ON] = day;
  proposed[CLOSURE_STATUS] = CLOSURE_CLOSED;
  proposed[RESOLUTION_APPROVED_ON] = dayPayload(save.register, RESOLUTION_APPROVED_ON, todayMelbourne(now));
  proposed[RESOLUTION_APPROVED_BY_UPN] = save.actorUpn;

  // ISS-260 (D-13-33): the report scrutiny before closure (G-60) is the approve step's own `Review` row. When an
  // investigation was required it is required here, so the approval and its scrutiny are one save and an
  // investigated incident can't close without it. Otherwise it is optional, and written when given.
  const rows: PendingDraft[] = [];
  const note = cleanNoteText(input.reviewNote ?? null, 'Your scrutiny');
  const investigated = row.fields[INVESTIGATION_REQUIRED] === INVESTIGATION_YES;
  if (!note.ok) refuse(save, note.code, 'reviewNote', note.message);
  else if (note.value !== '') rows.push(reviewRow(save, note.value));
  else if (investigated) {
    refuse(
      save,
      'review-required',
      'reviewNote',
      'An investigation was required, so record your scrutiny of the investigation report before approving. It goes on the trail as the review row.',
    );
  }
  return finish(save, proposed, { scope: 'closure' }, rows);
}

/**
 * Incidents (ISS-259, D-13-33): Oversight sends a readied incident back to the Care Manager, `Evaluating` →
 * `Actioning`, with a reason of at least `REASON_MIN` characters on its `StateChange` row. Only `Status` moves: the
 * closure assessment stays on the record for the Care Manager to correct, and *Ready for approval* runs again (a
 * new `Status → Evaluating` row, so the next preparer is whoever readies it then, D-13-L-19).
 */
export function buildReturnForRework(row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx?: ClosureContext): RecordVerbBuild {
  const save = begin('returnForRework', row, actor, input, now, ctx);
  requireReason(save);
  const proposed: Record<string, RecordFieldValue> = {};
  if (save.to) proposed[STATUS] = save.to;
  return finish(save, proposed, { scope: 'none' });
}

/**
 * Complaints, Feedback, CI: from `Actioning` or `Evaluating` → `Completed`, `ClosedOn` the close day, closed; the
 * closure columns from `set`. A CI is refused `not-approved` until `ciApprove` has run.
 */
export function buildClose(row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx?: ClosureContext): RecordVerbBuild {
  const save = begin('close', row, actor, input, now, ctx);
  const proposed: Record<string, RecordFieldValue> = { ...takeSet(save) };
  if (save.to) proposed[STATUS] = save.to;
  const day = closeDay(save);
  if (day !== null) proposed[CLOSED_ON] = day;
  proposed[CLOSURE_STATUS] = CLOSURE_CLOSED;
  return finish(save, proposed, { scope: 'closure', codeOf: { [RESOLUTION_APPROVED_BY_UPN]: 'not-approved' } });
}

/** CI: `ResolutionApprovedByUPN` = the actor; no `Status` change; refused on a `Completed` or `Cancelled` CI. */
export function buildCiApprove(row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx?: ClosureContext): RecordVerbBuild {
  const save = begin('ciApprove', row, actor, input, now, ctx);
  return finish(save, { [RESOLUTION_APPROVED_BY_UPN]: save.actorUpn }, { scope: 'none' });
}

/**
 * Any register: from `New`, `Actioning` or `Evaluating` → `Cancelled`, closed. `ClosedOn` is never written: a
 * cancelled record was never resolved (§1.8). The reason goes on every row.
 */
export function buildCancel(row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx?: ClosureContext): RecordVerbBuild {
  const save = begin('cancel', row, actor, input, now, ctx);
  requireReason(save);
  const proposed: Record<string, RecordFieldValue> = {};
  if (save.to) proposed[STATUS] = save.to;
  proposed[CLOSURE_STATUS] = CLOSURE_CLOSED;
  return finish(save, proposed, { scope: 'none' });
}

/** The kept ref as a canonical `INC-<id>` (`INC-P<n>` on a practice record), or `null`. */
function keptRefOf(row: RecordRow, raw: unknown): { ref: string; id: number } | null {
  const practice = isPracticeRef(row.ref);
  if (typeof raw !== 'string' || !(practice ? PRACTICE_REF_PATTERN : LIVE_REF_PATTERN).test(raw.trim())) return null;
  const parsed = practice ? parsePracticeRef(raw) : parseRef(raw);
  return parsed.register === row.register ? { ref: parsed.ref, id: parsed.id } : null;
}

/**
 * The kept record's checks (D-13.2-P-10), each its own refusal at the kept-ref input, all in one pass: the ref is
 * `INC-<digits>`; not this record; the caller read it (`ctx.kept`, by that ref or id); it is an incident; it is not
 * cancelled; it is not itself a duplicate (no chains). Returns the canonical ref, or `null` when refused.
 */
function checkKept(save: Save, kept: RecordRow | null | undefined): string | null {
  const at = DUPLICATE_OF_REF;
  const wanted = keptRefOf(save.row, save.input.keptRef);
  if (!wanted) {
    refuse(save, 'bad-ref', at, 'Duplicate of: enter the incident this one duplicates as INC- and its number, for example INC-11.');
    return null;
  }
  if (wanted.ref === save.row.ref) {
    refuse(save, 'dup-self', at, "An incident can't be a duplicate of itself. Enter the incident you are keeping.");
    return null;
  }
  if (!kept || (kept.ref !== wanted.ref && kept.id !== wanted.id)) {
    refuse(save, 'dup-not-found', at, `${wanted.ref} couldn't be read, so this can't be closed as its duplicate. Check the number.`);
    return null;
  }
  let fine = true;
  if (kept.register !== save.register) {
    refuse(save, 'dup-wrong-register', at, `${wanted.ref} isn't an incident. A duplicate is kept on its own register.`);
    fine = false;
  }
  if (kept.fields[STATUS] === S_CANCELLED) {
    refuse(save, 'dup-kept-cancelled', at, `${wanted.ref} is Cancelled. Keep a live incident in its place, not a cancelled one.`);
    fine = false;
  }
  const chain = kept.fields[DUPLICATE_OF_REF];
  if (!isBlank(chain)) {
    refuse(save, 'dup-chain', at, `${wanted.ref} is itself a duplicate of ${String(chain).trim()}. Close this one as a duplicate of ${String(chain).trim()} instead.`);
    fine = false;
  }
  return fine ? wanted.ref : null;
}

/**
 * Incidents (13.2-05, D-13.2-P-10; Oversight only, 13-22): from `New`, `Actioning` or `Evaluating` → `Cancelled`,
 * closed (the cancel's value), `DuplicateOfRef` = the kept record's canonical `INC-<id>`. `ClosedOn` is never
 * written (a cancel, §1.8) and `ExternalId` is never touched, so the intake reconcile still accounts for the ticket.
 *
 * Each `StateChange` row carries the reason "Duplicate of INC-n." and the person's optional note after it (screened
 * like any reason; no minimum, since the link is the reason). The kept record gets one `Note` row naming this
 * record and its ShiftCare ticket, returned apart as `keptSiblings` for the caller to post on the kept record's
 * trail. It has no `LogKind`: no live kind fits a cross-reference (the 16 are FIELDSET §3.4.1 obligations). Both
 * records' rows are one save: one `[a:<id>.<n>]` sequence, no tag twice.
 */
export function buildCloseAsDuplicate(row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx?: ClosureContext): DuplicateBuild {
  const save = begin('closeAsDuplicate', row, actor, input, now, ctx);
  const keptRef = checkKept(save, ctx?.kept);
  const note = save.reason;
  const proposed: Record<string, RecordFieldValue> = {};
  if (save.to) proposed[STATUS] = save.to;
  proposed[CLOSURE_STATUS] = CLOSURE_CLOSED;
  const rows: PendingDraft[] = [];
  if (keptRef !== null) {
    proposed[DUPLICATE_OF_REF] = keptRef;
    save.reason = `Duplicate of ${keptRef}.${note ? ` ${note}` : ''}`;
    const ticket = row.fields[EXTERNAL_ID];
    const named = typeof ticket === 'number' || (typeof ticket === 'string' && ticket.trim() !== '') ? String(ticket).trim() : '';
    rows.push({
      Title: `${save.verb} ${keptRef}: ${row.ref}`,
      ParentRef: keptRef,
      EntryType: NOTE,
      EntryTimestamp: toInstant(now),
      ActorUPN: save.actorUpn,
      ...(save.actor.siteUserId ? { ActorLookupId: save.actor.siteUserId } : {}),
      noteHead:
        `${row.ref} was closed as a duplicate of this record` +
        (named ? `. ${row.ref} holds ShiftCare ticket ${named}, so the daily reconcile still finds the ticket there.` : '.') +
        `\n${CLOSURE_LABELS[save.verb]} by ${save.actorUpn}.`,
      ...(note ? { noteTail: `Reason: ${note}` } : {}),
    });
  }
  const built = finish(save, proposed, { scope: 'none' }, rows);
  if (!built.ok) return built;
  return {
    ...built,
    siblings: built.siblings.filter((s) => s.ParentRef === row.ref),
    keptSiblings: built.siblings.filter((s) => s.ParentRef !== row.ref),
  };
}

/**
 * Any register (D-13-L-17): only `Completed` → `Actioning`; `ClosedOn` cleared (the old value kept as the row's
 * `OldValue`; Risk has none), open again. On Incidents the approval columns are left as they are (the next approval
 * is a new act). On CI, `ResolutionApprovedByUPN` is cleared too (D-13-21-2, ruled 13 Oct 2026), so `ciApprove`
 * must run again before close. `Cancelled` stays terminal.
 */
export function buildReopen(row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx?: ClosureContext): RecordVerbBuild {
  const save = begin('reopen', row, actor, input, now, ctx);
  requireReason(save);
  const proposed: Record<string, RecordFieldValue> = {};
  if (save.to) proposed[STATUS] = save.to;
  if (verbColumns(save.register, 'reopen').includes(CLOSED_ON)) proposed[CLOSED_ON] = null;
  proposed[CLOSURE_STATUS] = CLOSURE_OPEN;
  if (verbColumns(save.register, 'reopen').includes(RESOLUTION_APPROVED_BY_UPN)) proposed[RESOLUTION_APPROVED_BY_UPN] = null;
  return finish(save, proposed, { scope: 'none' });
}

/**
 * Risk review: `ReviewDate` = the next review day (required, strictly after Melbourne today), a `Review` row with
 * the person's text, `RiskAccepted` / `InsuranceChecked` from `set`. `RiskAcceptedByUPN` and `RiskAcceptedOn` are
 * stamped only when `RiskAccepted` changes to Yes, and cleared when it changes from Yes (D-13-21-4). No `Status`.
 */
export function buildRiskReview(row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx?: ClosureContext): RecordVerbBuild {
  const save = begin('riskReview', row, actor, input, now, ctx);
  const register = save.register;
  const proposed: Record<string, RecordFieldValue> = { ...takeSet(save) };
  const reviewLabel = fieldOf(register, REVIEW_DATE)?.label ?? REVIEW_DATE;

  const next = input.nextReviewOn;
  if (isBlank(next)) {
    refuse(save, 'required', REVIEW_DATE, `${reviewLabel}: enter the day of the next review.`);
    save.bad.add(REVIEW_DATE);
  } else if (typeof next !== 'string' || !dayNew(next.trim()).ok) {
    refuse(save, 'invalid-day', REVIEW_DATE, `${reviewLabel}: enter a real date as YYYY-MM-DD.`);
    save.bad.add(REVIEW_DATE);
  } else if (next.trim() <= todayMelbourne(now)) {
    refuse(save, 'review-not-after-today', REVIEW_DATE, `${reviewLabel} must be after today.`);
    save.bad.add(REVIEW_DATE);
  } else {
    proposed[REVIEW_DATE] = dayPayload(register, REVIEW_DATE, next.trim());
  }

  const was = row.fields[RISK_ACCEPTED];
  if (RISK_ACCEPTED in proposed) {
    const becomes = proposed[RISK_ACCEPTED];
    if (becomes === RISK_YES && was !== RISK_YES) {
      proposed[RISK_ACCEPTED_BY_UPN] = save.actorUpn;
      proposed[RISK_ACCEPTED_ON] = dayPayload(register, RISK_ACCEPTED_ON, todayMelbourne(now));
    } else if (was === RISK_YES && becomes !== RISK_YES) {
      proposed[RISK_ACCEPTED_BY_UPN] = null;
      proposed[RISK_ACCEPTED_ON] = null;
    }
  }

  const rows: PendingDraft[] = [];
  const note = cleanNoteText(input.reviewNote ?? null, 'The review');
  if (!note.ok) refuse(save, note.code, 'reviewNote', note.message);
  else if (note.value === '') refuse(save, 'required', 'reviewNote', 'Write what the review found.');
  else rows.push(reviewRow(save, note.value));

  return finish(save, proposed, { scope: 'own', skip: new Set(SET_BY_VERB.riskReview) }, rows);
}

/**
 * Risk retire: from `New`, `Actioning` or `Evaluating` → `Completed`, closed, no `ClosedOn` (Risk has none); a
 * `Review` row with the reason. Refused while a closure-required column is blank (a WHS hazard's
 * `ResolutionCommunicatedOn`, §3.4 "before closure"; the review answers, D-13-21-3).
 */
export function buildRiskRetire(row: RecordRow, actor: RecordActor, input: ClosureInput, now: Date, ctx?: ClosureContext): RecordVerbBuild {
  const save = begin('riskRetire', row, actor, input, now, ctx);
  requireReason(save);
  const proposed: Record<string, RecordFieldValue> = {};
  if (save.to) proposed[STATUS] = save.to;
  proposed[CLOSURE_STATUS] = CLOSURE_CLOSED;
  const rows = save.reason.length >= REASON_MIN ? [reviewRow(save, save.reason)] : [];
  return finish(save, proposed, { scope: 'closure' }, rows);
}

/** Every closure builder by verb key. */
export const CLOSURE_BUILDERS: Readonly<Record<ClosureVerb, ClosureBuilder>> = {
  readyForApproval: buildReadyForApproval,
  approveAndClose: buildApproveAndClose,
  returnForRework: buildReturnForRework,
  close: buildClose,
  ciApprove: buildCiApprove,
  riskReview: buildRiskReview,
  riskRetire: buildRiskRetire,
  cancel: buildCancel,
  closeAsDuplicate: buildCloseAsDuplicate,
  reopen: buildReopen,
};

/** Dispatch by verb key (the record page's one entry point). An unknown verb throws `RecordVerbError`. */
export function buildClosureVerb(
  verb: ClosureVerb,
  row: RecordRow,
  actor: RecordActor,
  input: ClosureInput,
  now: Date,
  ctx?: ClosureContext,
): RecordVerbBuild {
  if (!Object.prototype.hasOwnProperty.call(CLOSURE_BUILDERS, verb)) {
    throw new RecordVerbError('unknown-verb', `recordClosure: no closure verb '${String(verb)}'`);
  }
  return CLOSURE_BUILDERS[verb](row, actor, input, now, ctx);
}
